/**
 * The integrity check: both peers fingerprint their whole encounter on the
 * same schedule and swap the number.
 *
 * v1 does nothing clever with a mismatch. There is no rollback buffer to
 * rewind to and no authority to resync against, so the honest response to
 * "these two fights are no longer the same fight" is to say so and stop —
 * a silent divergence is much worse than an ended duel, because both players
 * keep flying a match that only exists on their own screen.
 *
 * A verdict is *sticky*: once `divergedAt` is set it stays set, even if a
 * later pair happens to agree again (which can happen, since a hash collision
 * or a self-correcting field is not evidence the fights re-converged).
 */

export type HashVerdict = "pending" | "match" | "mismatch";

export class HashLedger {
  private readonly local = new Map<number, string>();
  private readonly remote = new Map<number, string>();
  private diverged: number | null = null;
  private agreed = 0;

  /** The tick a divergence was first proved at, or `null` while the two fights agree. */
  get divergedAt(): number | null {
    return this.diverged;
  }

  /** How many scheduled hashes have been confirmed identical. */
  get matches(): number {
    return this.agreed;
  }

  private compare(tick: number): HashVerdict {
    const mine = this.local.get(tick);
    const theirs = this.remote.get(tick);
    if (mine === undefined || theirs === undefined) return "pending";
    this.local.delete(tick);
    this.remote.delete(tick);
    if (mine === theirs) {
      this.agreed += 1;
      return "match";
    }
    if (this.diverged === null) this.diverged = tick;
    return "mismatch";
  }

  recordLocal(tick: number, hash: string): HashVerdict {
    this.local.set(tick, hash);
    return this.compare(tick);
  }

  recordRemote(tick: number, hash: string): HashVerdict {
    this.remote.set(tick, hash);
    return this.compare(tick);
  }

  /** Scheduled hashes still waiting for their other half. */
  get pending(): number {
    return this.local.size + this.remote.size;
  }
}
